import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import config from "../../config";
import Swal from "sweetalert2";
import { AnimatePresence, motion } from "framer-motion";
import CryptoJS from "crypto-js";

function Login() {
  const [schoolCode, setSchoolCode] = useState("");
  const [schoolPass, setSchoolPass] = useState("");

  const [loading, setLoading] = useState(false);

  const [captcha, setCaptcha] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);

  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const SECRET_KEY = config.SECRET_KEY;
  function generateOneTimeToken() {
    const timestamp = Date.now();
    const nonce = CryptoJS.lib.WordArray.random(16).toString();
    const payload = `${timestamp}:${nonce}`;
    const signature = CryptoJS.HmacSHA256(payload, SECRET_KEY).toString();

    return `${timestamp}:${nonce}:${signature}`;
  }
  // login pageload API call
  useEffect(() => {
    sessionStorage.removeItem("intermediateAdminToken");
    sessionStorage.removeItem("schoolCode");
    generateCaptcha();
  }, []);
  // Generate Smart Math Captcha
  const generateCaptcha = () => {
    const operations = ["+", "-", "*"];
    let num1 = Math.floor(Math.random() * 10); //+ 10;
    let num2 = Math.floor(Math.random() * 10); //+ 10;
    const op = operations[Math.floor(Math.random() * operations.length)];

    if (op === "-" && num1 < num2) {
      [num1, num2] = [num2, num1];
    }

    let answer;
    switch (op) {
      case "+":
        answer = num1 + num2;
        break;
      case "-":
        answer = num1 - num2;
        break;
      case "*":
        answer = num1 * num2;
        break;
    }
    const question = `${num1} ${op} ${num2} = ?`;
    setCaptchaQuestion(question);
    setCaptcha(answer);
    setUserCaptcha("");
    setIsCaptchaValid(false);
    drawCaptcha(question);
  };
  // Draw CAPTCHA on Canvas
  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background
    ctx.fillStyle = "#f0f0f0";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add noise
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.fillRect(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        1,
        1
      );
    }

    // Captcha text
    ctx.font = "italic bold 28px 'Courier New', monospace";
    ctx.fillStyle = `rgb(${Math.random() * 100}, ${Math.random() * 100}, ${
      Math.random() * 100
    })`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  };
  // Captcha input handler
  const validateCaptcha = (e) => {
    const val = e.target.value;
    setUserCaptcha(val);
    setIsCaptchaValid(parseInt(val) === parseInt(captcha));
  };
  // Login handler with captcha validation
  const handleLoginWithCaptcha = async () => {
    if (!schoolCode) {
      Swal.fire("Warning", "Please enter admin id.", "warning");
      return;
    }
    if (!schoolPass) {
      Swal.fire("Warning", "Please enter password.", "warning");
      return;
    }
    if (!isCaptchaValid) {
      Swal.fire({
        icon: "error",
        title: "Invalid CAPTCHA",
        text: "Please enter the correct CAPTCHA.",
        confirmButtonColor: "#d33",
      });
      generateCaptcha();
      return;
    }

    await handleLogin();
    generateCaptcha();
  };
  // Login API call
  const handleLogin = async () => {
    setLoading(true);
    try {
      const tokeng = generateOneTimeToken();
      const response = await fetch(
        `${config.API_BASE_URL}/AdminAuthenticationIntermediate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Private-Key": tokeng,
          },
          body: JSON.stringify({
            loginid: schoolCode,
            password: schoolPass,
          }),
        }
      );

      const data = await response.json();
      // console.log("Login response:", data);
      if (response.ok) {
        sessionStorage.setItem("schoolCode", data.data.SCHOOL_CODE);
        sessionStorage.setItem("intermediateAdminToken", data.data.token);
        navigate("/intermediate-admin-dashboard");
      } else {
        Swal.fire(
          "Error",
          data?.message || "Invalid login credentials",
          "error"
        );
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong. Try again later.", "error");
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-scroller">
      <div className="container-fluid page-body-wrapper full-page-wrapper">
        <div className="content-wrapper d-flex align-items-center auth px-0 bg-green">
          <div className="row w-100 mx-0">
            <div className="col-lg-4 mx-auto">
              <div className="auth-form-light text-left py-4 px-3 px-sm-4">
                <div className="brand-logo" style={{ display: "flex" }}>
                  <img
                    src="../assets/images/jac.png"
                    alt="logo"
                    style={{ height: 50, width: 50, marginRight: 10 }}
                  />
                  <p
                    className="font-weight-light"
                    style={{
                      margin: "auto",
                      textAlign: "center",
                      fontSize: 19,
                      fontWeight: 600,
                    }}
                  >
                    Jharkhand Academic Council Ranchi
                  </p>
                </div>
                <h3 className="text-center">Admin Portal Intermediate</h3>
                <form
                  className="pt-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleLoginWithCaptcha();
                  }}
                >
                  <div className="form-group">
                    <h6 className="font-weight-light">Enter Admin ID</h6>
                    <input
                      type="text"
                      className="form-control form-control-xs"
                      placeholder="Enter Accounts ID"
                      value={schoolCode}
                      onChange={(e) => setSchoolCode(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <h6 className="font-weight-light">Enter Password</h6>
                    <input
                      type="password"
                      className="form-control form-control-xs"
                      placeholder="Enter Password"
                      value={schoolPass}
                      onChange={(e) => setSchoolPass(e.target.value)}
                    />
                  </div>

                  <div className="form-group mb-3 text-center">
                    <canvas
                      ref={canvasRef}
                      width="200"
                      height="40"
                      style={{ border: "1px solid #ddd" }}
                    />
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="btn btn-secondary ms-2"
                      style={{ marginTop: "-33px" }}
                    >
                      ↻ Refresh
                    </button>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Enter CAPTCHA</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Enter CAPTCHA"
                      value={userCaptcha}
                      onChange={validateCaptcha}
                    />
                  </div>

                  <div className="mt-1">
                    <button
                      type="submit"
                      className="btn btn-block bg-success btn-xs text-white"
                      style={{ padding: "10px" }}
                      disabled={loading}
                    >
                      {loading ? "Logging in..." : "Login"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
